/**
 * Centralized theme constants for consistent styling across the application.
 */

// ============================================================================
// COLORS
// ============================================================================

export const colors = {
  // Brand colors
  primary: '#003D4F',         // Dark teal - primary buttons, main actions
  primaryLight: '#48a1ad',    // Light teal - secondary buttons, accents, links
  primaryHover: '#156C86',    // Hover state for primary elements

  /** Left navigation rail (matches brand teal) */
  sidebar: {
    background: '#48a1ad',
    selected: '#3d8388',
    hover: 'rgba(255, 255, 255, 0.1)',
    subtleText: 'rgba(255, 255, 255, 0.65)',
    divider: 'rgba(255, 255, 255, 0.2)',
  },
  
  // Neutral colors
  text: {
    primary: '#333333',       // Main text color
    secondary: '#666666',     // Secondary/muted text
    disabled: '#999999',      // Disabled text
    light: '#ffffff',         // Light text (on dark backgrounds)
  },
  
  // Background colors
  background: {
    page: '#f5f5f5',          // Page background
    card: '#ffffff',          // Card/paper background
    hover: 'rgba(0,0,0,0.04)', // Hover state background
    disabled: '#f5f5f5',      // Disabled input background
  },
  
  // Border colors
  border: {
    light: '#d9dede',         // Light border (header, cards)
    medium: '#e0e0e0',        // Medium border (inputs, dividers)
    dark: '#d0d0d0',          // Dark border (buttons)
  },
  
  // Semantic colors
  success: '#4caf50',         // Success states
  error: '#f44336',           // Error states
  warning: '#ff9800',         // Warning states
  info: '#2196f3',            // Info states (liked icon)
  
  // Feedback colors
  liked: '#2196f3',           // Thumbs up
  disliked: '#f44336',        // Thumbs down
} as const;

// ============================================================================
// TYPOGRAPHY
// ============================================================================

export const typography = {
  fontFamily: {
    primary: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    heading: '"Bitter", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  
  fontSize: {
    xs: '0.75rem',      // 12px
    sm: '0.875rem',     // 14px
    base: '0.9rem',     // ~14.4px - commonly used
    md: '0.95rem',      // ~15.2px
    lg: '1rem',         // 16px
    xl: '1.25rem',      // 20px
    '2xl': '1.5rem',    // 24px
    '3xl': '1.75rem',   // 28px
    '4xl': '2rem',      // 32px
  },
  
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.6,
  },
} as const;

// ============================================================================
// SPACING
// ============================================================================

export const spacing = {
  header: {
    height: '81px',           // AppBar actual height
    minHeight: '96px',        // Toolbar min-height
    paddingY: 2,              // py value for Toolbar
  },
  
  container: {
    /** Main content top padding below the app bar (`PageLayout` default). */
    paddingTop: '24px',
    paddingBottom: 4,         // MUI spacing unit (32px)
    marginTop: '81px',        // Offset for fixed header
  },
} as const;

export const pageLayoutDefaults = {
  maxWidth: 'xl' as const,
  contentPaddingTop: spacing.container.paddingTop,
} as const;

// ============================================================================
// COMMON COMPONENT STYLES
// ============================================================================

/**
 * Common TextField styles used throughout the application.
 */
export const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1,
    backgroundColor: colors.background.card,
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.primaryLight,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.primaryLight,
      borderWidth: 2,
    },
  },
} as const;

/**
 * Primary button styles (filled, dark teal).
 */
export const primaryButtonStyles = {
  backgroundColor: colors.primary,
  color: colors.text.light,
  textTransform: 'none' as const,
  fontWeight: typography.fontWeight.medium,
  borderRadius: '4px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: colors.primaryHover,
    boxShadow: 'none',
  },
} as const;

/**
 * Secondary button styles (outlined, light teal).
 */
export const secondaryButtonStyles = {
  border: `1.5px solid ${colors.primaryLight}`,
  color: colors.primaryLight,
  textTransform: 'none' as const,
  fontWeight: typography.fontWeight.medium,
  borderRadius: '4px',
  '&:hover': {
    border: `1.5px solid ${colors.primaryLight}`,
    backgroundColor: 'rgba(72, 161, 173, 0.08)',
  },
} as const;

/**
 * Outlined button styles (outlined, dark teal border).
 */
export const outlinedButtonStyles = {
  border: `1.5px solid ${colors.primary}`,
  color: colors.primary,
  textTransform: 'none' as const,
  fontWeight: typography.fontWeight.medium,
  borderRadius: 0,
  '&:hover': {
    border: `1.5px solid ${colors.primary}`,
    backgroundColor: colors.background.disabled,
  },
} as const;

/**
 * Text button styles (no border, used in header).
 */
export const textButtonStyles = {
  color: colors.text.secondary,
  textTransform: 'none' as const,
  fontSize: typography.fontSize.base,
  fontWeight: typography.fontWeight.medium,
  '&:hover': {
    backgroundColor: colors.background.hover,
  },
} as const;

/**
 * Card/Paper elevation styles.
 */
export const cardStyles = {
  borderRadius: 2,
  backgroundColor: colors.background.card,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
} as const;

/**
 * AppBar styles.
 */
export const appBarStyles = {
  backgroundColor: colors.background.card,
  borderBottom: `1px solid ${colors.border.light}`,
  boxShadow: 'none',
  top: 0,
  zIndex: 1100,
} as const;

